/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ImportResultsPanel.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Extensions module.
----------------------------------------------------------------*/


import { CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';

interface ImportResults {
    total: number;
    created: number;
    updated: number;
    skipped: number;
    deleted?: number;
    errors: string[];
    elapsedTime?: number;
}

interface ImportResultsPanelProps {
    results: ImportResults;
    elapsedTime: number | null;
    t: (key: string, fallback?: string) => string;
}

export function ImportResultsPanel({ results, elapsedTime, t }: ImportResultsPanelProps) {
    return (
        <div className="space-y-4">
            {/* Success Banner */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
                            {t('extensions.import.success')}
                        </p>
                        <p className="text-xs text-green-700 dark:text-green-300">
                            {results.created} {t('extensions.import.extensionsCreated')}, {results.updated} {t('extensions.import.extensionsUpdated')}
                            {results.deleted && results.deleted > 0 && `, ${results.deleted} ${t('extensions.import.extensionsDeleted')}`}
                            {results.skipped > 0 && `, ${results.skipped} ${t('extensions.import.extensionsSkipped')}`}
                        </p>
                    </div>
                </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Created */}
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                        <span className="text-sm font-medium text-green-900 dark:text-green-100">{t('extensions.import.created')}</span>
                    </div>
                    <div className="text-2xl font-bold text-green-700 dark:text-green-300">{results.created}</div>
                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">{t('extensions.import.createdDesc')}</div>
                </div>

                {/* Updated */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">{t('extensions.import.updated')}</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{results.updated}</div>
                    <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">{t('extensions.import.updatedDesc')}</div>
                </div>

                {/* Deleted (conditional) */}
                {results.deleted !== undefined && results.deleted > 0 && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                            <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                            <span className="text-sm font-medium text-red-900 dark:text-red-100">{t('extensions.import.deleted')}</span>
                        </div>
                        <div className="text-2xl font-bold text-red-700 dark:text-red-300">{results.deleted}</div>
                        <div className="text-xs text-red-600 dark:text-red-400 mt-1">{t('extensions.import.deletedDesc')}</div>
                    </div>
                )}

                {/* Skipped */}
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                        <span className="text-sm font-medium text-yellow-900 dark:text-yellow-100">{t('extensions.import.skipped')}</span>
                    </div>
                    <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{results.skipped}</div>
                    <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">{t('extensions.import.skippedDesc')}</div>
                </div>

                {/* Total */}
                <div className="p-4 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                        <Info className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('extensions.import.total')}</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">{results.total}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{t('extensions.import.totalDesc')}</div>
                </div>
            </div>

            {/* Elapsed Time */}
            {elapsedTime !== null && (
                <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <svg className="h-5 w-5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                                {t('extensions.import.elapsedTime') || 'Processing Time'}
                            </span>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                                {elapsedTime >= 60000
                                    ? `${Math.floor(elapsedTime / 60000)}m ${((elapsedTime % 60000) / 1000).toFixed(1)}s`
                                    : `${(elapsedTime / 1000).toFixed(2)}s`}
                            </div>
                            <div className="text-xs text-purple-600 dark:text-purple-400">
                                {results.total > 0 && elapsedTime > 0 && (
                                    <>{Math.round(results.total / (elapsedTime / 1000))} {t('extensions.import.extensionsPerSec') || 'extensions/sec'}</>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Errors */}
            {results.errors && results.errors.length > 0 && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-start gap-3">
                        <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                        <div className="space-y-2 flex-1">
                            <p className="text-sm font-medium text-red-900 dark:text-red-100">
                                {t('extensions.import.errors')} ({results.errors.length})
                            </p>
                            <ul className="text-xs text-red-700 dark:text-red-300 space-y-1 max-h-40 overflow-y-auto">
                                {results.errors.map((err, idx) => (
                                    <li key={idx}>� {err}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
